import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { CheckCircle, LayoutDashboard, Users, Sparkles, BarChart3, Home as HomeIcon } from 'lucide-react';
import SeoHead from '@/components/SeoHead';
import { useAuth } from '@/contexts/AuthContext';
// cmsService removed as we are using strict hardcoded copy

const LegacyHome = () => {
    const { user } = useAuth();

    return (
        <div className="min-h-screen bg-white">
            <SeoHead
                title="Servicios Futura - Plataforma Inmobiliaria | El Salvador"
                description="Servicios Futura es la plataforma inmobiliaria diseñada para agentes en El Salvador. Publica propiedades y recibe leads directos."
                schema={{
                    "@context": "https://schema.org",
                    "@type": "Organization",
                    "name": "Servicios Futura",
                    "url": "https://serviciosfutura.com",
                    "logo": "https://serviciosfutura.com/logo-main.png",
                    "contactPoint": {
                        "@type": "ContactPoint",
                        "telephone": "+503-6982-6270",
                        "contactType": "sales",
                        "areaServed": "SV",
                        "availableLanguage": "Spanish"
                    }
                }}
            />

            {/* HERO SECTION */}
            <section className="relative py-20 lg:py-32 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white overflow-hidden">
                <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80')] bg-cover bg-center opacity-10"></div>
                <div className="container mx-auto px-4 relative z-10 text-center">
                    <h1 className="text-4xl md:text-6xl font-extrabold mb-6 tracking-tight leading-[1.1] max-w-5xl mx-auto text-white">
                        Publica propiedades, recibe leads directos y gestiona tu negocio todos los días.
                    </h1>
                    <p className="text-xl md:text-2xl font-light text-slate-200 mb-8 max-w-3xl mx-auto">
                        Servicios Futura es la plataforma inmobiliaria diseñada para agentes en El Salvador que quieren dejar de depender solo de comisiones y empezar a operar como verdaderos profesionales.
                    </p>

                    <ul className="text-left max-w-2xl mx-auto mb-10 space-y-4 text-lg text-slate-200 ">
                        <li className="flex items-start gap-3">
                            <span className="text-blue-400 font-bold mt-1">•</span>
                            <span>Publica tus propiedades en minutos y muéstralas con una presentación profesional.</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <span className="text-blue-400 font-bold mt-1">•</span>
                            <span>Recibe prospectos directos sin intermediarios ni comisiones ocultas.</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <span className="text-blue-400 font-bold mt-1">•</span>
                            <span>Organiza tus seguimientos diarios para no volver a perder un cliente caliente.</span>
                        </li>
                    </ul>

                    <div className="flex flex-col sm:flex-row justify-center gap-4">
                        {user ? (
                            <Link to="/dashboard">
                                <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-8 py-6 text-lg shadow-lg hover:shadow-blue-500/20 transition-all">
                                    Crear cuenta gratis y publicar hoy
                                </Button>
                            </Link>
                        ) : (
                            <Link to="/register">
                                <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-8 py-6 text-lg shadow-lg hover:shadow-blue-500/20 transition-all">
                                    Crear cuenta gratis y publicar hoy
                                </Button>
                            </Link>
                        )}
                        <Link to="/subscription">
                            <Button size="lg" className="bg-white text-slate-900 hover:bg-gray-100 font-bold px-8 py-6 text-lg border-0 shadow-lg">
                                Ver planes y precios
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>

            {/* TRUST STRIP */}
            <section className="bg-blue-900 border-t border-blue-800 py-6">
                <div className="container mx-auto px-4 text-center">
                    <p className="text-blue-100 font-medium text-lg tracking-wide">
                        Sin comisiones · Cancela cuando quieras · Pensado para el mercado inmobiliario de El Salvador.
                    </p>
                </div>
            </section>

            {/* SECCIÓN: CÓMO FUNCIONA */}
            <section className="py-20 bg-slate-50">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Trabaja tu negocio inmobiliario en 3 pasos diarios.</h2>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto relative mb-12">
                        {/* Connector Line (Desktop) with Arrows */}
                        <div className="hidden md:block absolute top-[48px] left-[15%] right-[15%] h-0.5 bg-gradient-to-r from-blue-200 via-blue-400 to-blue-200 -z-0">
                            <div className="absolute right-0 -top-1.5 w-3 h-3 border-t-2 border-r-2 border-blue-400 rotate-45"></div>
                        </div>

                        {[
                            { step: "1", title: "Publica lo que estás vendiendo.", desc: "Sube tus propiedades con fotos, precios y detalles claros. Deja tu inventario listo para mostrarlo a cualquier cliente hoy." },
                            { step: "2", title: "Responde leads y agenda seguimientos.", desc: "Cada contacto nuevo entra a tu CRM. Registra llamadas, notas y próximos pasos para saber exactamente a quién responder hoy." },
                            { step: "3", title: "Comparte y mide tus resultados.", desc: "Envía enlaces de propiedades y listas, y revisa cuánto inventario estás moviendo cada mes para ajustar tu estrategia." }
                        ].map((item, idx) => (
                            <div key={idx} className="relative z-10 flex flex-col items-center text-center">
                                <div className="w-24 h-24 bg-white rounded-full border-4 border-blue-600 flex items-center justify-center text-3xl font-bold text-blue-600 mb-6 shadow-sm">
                                    {item.step}
                                </div>
                                <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
                                <p className="text-gray-600 px-4">{item.desc}</p>
                            </div>
                        ))}
                    </div>

                    <div className="text-center">
                        <Link to="/register">
                            <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-10 py-6 text-lg shadow-lg">
                                Crear cuenta gratis
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>

        </div>
    );
};

const Home = () => {
    return <LegacyHome />;
};

export default Home;
